import { useState } from 'react';
import { Box, Typography, Grid, Autocomplete, TextField, CircularProgress, Chip, Divider, Paper } from '@mui/material';
import { Building2, Users, IndianRupee, Activity, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchools, useCompareSchools } from '@/hooks/useSchools';

export function SchoolComparisonPage() {
  usePageMeta('Compare Schools', [
    { label: 'Schools', path: '/schools' },
    { label: 'Compare' },
  ]);

  const navigate = useNavigate();
  const { schools, isLoading: isSchoolsLoading } = useSchools();
  const [selectedIds, setSelectedIds] = useState([]);
  const { comparisonData, isLoading: isComparisonLoading } = useCompareSchools(selectedIds, schools);

  const handleSchoolChange = (event, newValue) => {
    // Max 4 schools for comparison
    if (newValue.length <= 4) {
      setSelectedIds(newValue.map(s => s.id));
    }
  };

  const selectedOptions = schools.filter(s => selectedIds.includes(s.id));

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Compare Schools"
        subtitle="Compare key metrics across multiple branches (Max 4)."
        backPath="/schools"
        backLabel="Back to Schools"
      />

      <WidgetContainer sx={{ mt: 3, mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Select Schools to Compare</Typography>
        <Autocomplete
          multiple
          limitTags={4}
          id="school-compare-select"
          options={schools}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedOptions}
          onChange={handleSchoolChange}
          loading={isSchoolsLoading}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Search and select schools..." />
          )}
        />
      </WidgetContainer>

      {isComparisonLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!isComparisonLoading && comparisonData.length > 0 && (
        <Grid container spacing={2}>
          {comparisonData.map(({ school, summary }) => (
            <Grid xs={12} sm={6} md={12 / Math.max(comparisonData.length, 1)} key={school.id}>
              <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 3, border: `1px solid ${tokens.colors.border}`, height: '100%' }}>
                
                {/* Header */}
                <Box sx={{ p: 3, bgcolor: tokens.colors.primary[50], borderBottom: `1px solid ${tokens.colors.border}`, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} color="primary.main">{school.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{school.city}</Typography>
                </Box>

                {/* Metrics */}
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Users size={14} /> Total Students
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>{school.studentCount.toLocaleString()}</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IndianRupee size={14} /> Monthly Collection
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>₹{summary.thisMonthCollection.toLocaleString()}</Typography>
                    <Typography variant="body2" color="success.main" fontWeight={500} sx={{ mt: 0.5 }}>{summary.collectionPercentage}% collected</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IndianRupee size={14} /> Pending Fees
                    </Typography>
                    <Typography variant="h5" fontWeight={600} color="error.main" sx={{ mt: 0.5 }}>₹{summary.pendingFees.toLocaleString()}</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Activity size={14} /> Avg Attendance
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>{summary.averageAttendance}%</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUp size={14} /> New Admissions
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>{summary.newAdmissions}</Typography>
                  </Box>

                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {!isComparisonLoading && comparisonData.length === 0 && (
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center', 
            py: 8, 
            color: tokens.colors.text.secondary 
          }}
        >
          <Building2 size={48} style={{ opacity: 0.5, marginBottom: 16 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">No schools selected</Typography>
          <Typography variant="body2">Select at least one school from the dropdown above to view metrics.</Typography>
        </Box>
      )}

    </Box>
  );
}
